import type { Adapter, AdapterConfig } from "./adapter";
import { LineProtocolBinding, registerAdapter } from "./adapter";
import type { ConnectionState } from "../core/messages";
import { timers } from "../core/timers";

/**
 * WebSocket adapter — for devices/services exposing a `wss://` endpoint.
 * Text frames are treated as lines (same codec as serial). Reconnects
 * with capped exponential backoff via worker timers so backgrounded
 * tabs keep the link alive.
 */
function isLocalHostname(hostname: string): boolean {
  if (hostname === "localhost" || hostname === "[::1]" || hostname.endsWith(".local")) return true;
  if (/^127\./.test(hostname) || /^10\./.test(hostname) || /^192\.168\./.test(hostname)) return true;
  const match = /^172\.(\d+)\./.exec(hostname);
  if (match && Number(match[1]) >= 16 && Number(match[1]) <= 31) return true;
  return hostname.startsWith("[fc") || hostname.startsWith("[fd") || hostname.startsWith("[fe8");
}

export class WebSocketAdapter implements Adapter {
  state: ConnectionState = "idle";
  private socket: WebSocket | null = null;
  private binding: LineProtocolBinding;
  private closing = false;
  private retryMs = 1000;
  private retryTimer: number | null = null;
  private attempt = 0;
  private lastError: string | null = null;

  constructor(private config: AdapterConfig) {
    this.binding = new LineProtocolBinding(config, (line) => {
      if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(line + "\n");
    });
  }

  async connect(): Promise<void> {
    const url = this.config.options.url;
    if (!url) {
      this.setState("error", 'websocket transport requires a url attribute, e.g. url="wss://..."');
      return;
    }
    let endpoint: URL;
    try {
      endpoint = new URL(url);
    } catch {
      this.setState("error", "WebSocket URL is invalid.");
      return;
    }
    if (endpoint.protocol !== "ws:" && endpoint.protocol !== "wss:") {
      this.setState("error", "WebSocket URL must use ws:// or wss://.");
      return;
    }
    if (
      endpoint.protocol === "ws:" &&
      typeof location !== "undefined" &&
      location.protocol === "https:" &&
      !isLocalHostname(endpoint.hostname)
    ) {
      this.setState("error", "A secure page requires wss:// for non-local WebSocket servers.");
      return;
    }
    const attempt = ++this.attempt;
    this.closing = false;
    this.lastError = null;
    if (this.retryTimer !== null) timers.clear(this.retryTimer);
    this.retryTimer = null;
    const previous = this.socket;
    this.socket = null;
    previous?.close();
    this.binding.detach();
    this.open(url, attempt);
  }

  async disconnect(): Promise<void> {
    this.attempt += 1;
    this.closing = true;
    if (this.retryTimer !== null) timers.clear(this.retryTimer);
    this.retryTimer = null;
    this.binding.detach();
    this.socket?.close();
    this.socket = null;
    this.setState("disconnected");
  }

  private open(url: string, attempt: number): void {
    if (this.closing || attempt !== this.attempt) return;
    this.setState("connecting");
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch (err) {
      this.setState("error", err instanceof Error ? err.message : String(err));
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      if (this.socket !== socket || attempt !== this.attempt) return;
      this.retryMs = 1000;
      this.lastError = null;
      this.binding.attach();
      this.setState("connected");
      this.binding.requestDescriptor();
      timers.setTimeout(() => {
        if (this.socket === socket && attempt === this.attempt) this.config.onDescriptor?.(null);
      }, 2000);
    };
    socket.onmessage = (event) => {
      if (this.socket !== socket || attempt !== this.attempt) return;
      if (typeof event.data !== "string") return;
      for (const line of event.data.split("\n")) {
        const trimmed = line.replace(/\r$/, "");
        if (trimmed) this.binding.handleLine(trimmed);
      }
    };
    socket.onclose = () => {
      if (this.socket !== socket || attempt !== this.attempt) return;
      this.socket = null;
      this.binding.detach();
      if (this.closing) return;
      const retry = `Reconnecting in ${Math.round(this.retryMs / 1000)}s…`;
      this.setState(this.lastError ? "error" : "disconnected", this.lastError ? `${this.lastError} ${retry}` : retry);
      this.retryTimer = timers.setTimeout(() => this.open(url, attempt), this.retryMs);
      this.retryMs = Math.min(this.retryMs * 2, 30000);
    };
    socket.onerror = () => {
      if (this.socket !== socket || attempt !== this.attempt) return;
      const local = (() => {
        try {
          return isLocalHostname(new URL(url).hostname);
        } catch {
          return false;
        }
      })();
      this.lastError = local
        ? "Local WebSocket unavailable. In Chromium, allow Local Network access; other browsers may require wss://."
        : "WebSocket connection failed. Check the endpoint, TLS certificate, and server Origin policy.";
      this.setState("error", this.lastError);
    };
  }

  private setState(state: ConnectionState, detail?: string): void {
    this.state = state;
    this.config.onState(state, detail);
  }
}

registerAdapter({
  name: "websocket",
  fields: [
    { name: "url", label: "URL", placeholder: "wss://host/path", persist: true, cleanUrl: true },
  ],
  requiresGesture: false,
  supported: () => typeof WebSocket !== "undefined",
  create: (config) => new WebSocketAdapter(config),
});
